/**
 * Google Classic Analytics integration module.
 * @module
 */

var _ = require('optimizely-client-fn');

var JSONEnv = require('core/lib/json');
var Logger = require('core/utils/logger');
var Poll = require('core/utils/poll');
var RedirectUtil = require('core/utils/redirect');
var trackingPolicies = require('core/constants/tracking_policies');
var WindowEnv = require('core/env/window');

var VENDOR = 'google_classic_analytics';
var CUSTOM_VARIABLE_NAME = 'Optimizely';

// Global variable name used by Google Classic Analytics.
var GAQ = '_gaq';

/*
 * From the Custom Variables documentation
 * (https://developers.google.com/analytics/devguides/collection/gajs/gaTrackingCustomVariables#varTypes):
 * The total combined length of any custom variable name and value may not exceed 128 characters.
 */
var MAX_BYTES = 128;
var VALUE_LENGTH = MAX_BYTES - CUSTOM_VARIABLE_NAME.length;

var generateAnalyticsValue = require('core/plugins/helpers/generate_analytics_value');

/**
 * Construct a trackLayerDecision for Google Classic Analytics.
 * @param {DecisionData} decisionData
 * @return {Promise} Resolves when tracked with Google Classic Analytics API, or error occurs (in which case the error is logged)
 */
var trackLayerDecisionGoogleClassic = function(decisionData:DecisionData): Promise<any> {
  var settings = decisionData.integrationSettings;

  if (!settings || !settings[VENDOR]) {
    return;
  }

  if (!decisionData.experimentId || !decisionData.variationId) {
    return;
  }

  var layer = {
    id: decisionData.layerId,
    name: decisionData.layerName,
    policy: decisionData.layerPolicy,
    integrationStringVersion: decisionData.integrationStringVersion,
  };

  var experiment = {
    id: decisionData.experimentId,
    name: decisionData.experimentName,
  };

  var variation = {
    id: decisionData.variationId,
    name: decisionData.variationName,
  };

  var isLayerHoldback = decisionData.isLayerHoldback;

  var decisionString = generateAnalyticsValue.generateAnalyticsString(layer, experiment, variation, isLayerHoldback, VALUE_LENGTH, true);
  if (!decisionString) {
    return;
  }

  return trackDecisionWithGoogleAnalyticsClassic(decisionString, settings[VENDOR])['catch'](function(e) {
    Logger.warn('Tracker for', VENDOR, 'failed:', e);
  });
};

var trackDecisionWithGoogleAnalyticsClassic = function(decisionString, googleAnalyticsSettings) {
  var MAX_RETRIES = 50,
      WAIT_TIME = __TEST__ ? 5 : 200;

  return Poll.pollFor(function isReadyToTrack() {
    var googleAnalyticsQueue = WindowEnv.getGlobal(GAQ);

    // If the global has yet to be instantiated or has yet to be transformed to a _gaq Global Object
    // (https://developers.google.com/analytics/devguides/collection/gajs/methods/gaJSApi_gaq), then bail to not
    // mess with a customer's implementation of Google Classic Analytics.
    if (!googleAnalyticsQueue || _.isArray(googleAnalyticsQueue)) {
      return;
    }
    return googleAnalyticsQueue;
  }, WAIT_TIME, MAX_RETRIES)
  .then(function track(googleAnalyticsQueue) {
    var optLabel = null;
    var optValue = null;
    var optNoninteraction = true;

    // Optional integration field.
    var tracker = googleAnalyticsSettings.google_analytics_tracker;
    var prefix = tracker ? tracker + '.' : '';

    // Required integration field.
    var customVariableSlot = parseInt(googleAnalyticsSettings.google_analytics_slot, 10);
    if (isNaN(customVariableSlot)) {
      Logger.warn(
        'Google Analytics custom variable slot cannot be parsed to an integer: "',
        googleAnalyticsSettings.google_analytics_slot, '".  No data can be sent to Google Analytics.'
      );
      return;
    }

    // If there is effectiveReferrer then user was redirected from another page/experiment.
    // We need to use that page's referrer for correct tracking.
    // Need to call _setReferrerOverride before custom variables are set:
    // http://drupal.org/node/1489552
    var effectiveReferrer = RedirectUtil.getReferrer();
    if (effectiveReferrer) {
      googleAnalyticsQueue.push([prefix + '_setReferrerOverride', effectiveReferrer]);
    }
    googleAnalyticsQueue.push(
      // Set the custom variable slot before executing the trackEvent call.
      // Reference:
      // https://developers.google.com/analytics/devguides/collection/gajs/gaTrackingCustomVariables
      [prefix + '_setCustomVar', customVariableSlot, CUSTOM_VARIABLE_NAME, decisionString],
      // Reference:
      // https://developers.google.com/analytics/devguides/collection/gajs/methods/gaJSApiEventTracking
      [prefix + '_trackEvent', 'Optimizely', 'Assigned to Campaign', optLabel, optValue, optNoninteraction]
    );
  });
};

var trackerGoogleClassic = {
  preRedirectPolicy: trackingPolicies.PreRedirectPolicies.PERSIST_BEFORE_REDIRECT,
  postRedirectPolicy: trackingPolicies.PostRedirectPolicies.TRACK_IMMEDIATELY,
  nonRedirectPolicy: trackingPolicies.NonRedirectPolicies.TRACK_IMMEDIATELY,
  trackLayerDecision: trackLayerDecisionGoogleClassic,
  serializeSettings: JSONEnv.stringify,
  deserializeSettings: JSONEnv.parse,
};

module.exports = function(PluginManager) {
  PluginManager.registerAnalyticsTracker('google_classic_analytics', trackerGoogleClassic);
};

if (__TEST__) {
  module.exports.plugin = trackerGoogleClassic;
}
